import React, { useEffect, useState, createElement, Component } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Loader2, Copy, Check, Code } from 'lucide-react';
import { LayoutTemplateIcon, SearchIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { signupFormsApi, formConfigsApi, emailTemplatesApi, newslettersApi, extractLookupId, formTemplatesLibraryApi, FormTemplateRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { FormBuilder } from '../components/builder/FormBuilder';
import { FormBlock, FormTheme, FormBuilderSchema, DEFAULT_FORM_THEME } from '../components/builder/types';
/** Convert a string to a URL-safe slug */
function toSlug(str: string): string {
  return str.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
}
/** Escape a string for safe embedding inside single-quoted JS strings */
function escapeForJSString(str: string): string {
  return str.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/<\/script>/gi, '<\\/script>');
}
/** Data baked into the embed script from linked Form Config / Email Template / Newsletter */
interface EnrichmentData {
  emailHtml: string;
  emailSubject: string;
  newsletterId: string;
  region: string;
  redirectUrl: string;
  appOrigin: string;
}
/** Fetch linked email template + newsletter data for a given signup form ID */
async function fetchEnrichmentData(formId: string): Promise<EnrichmentData | null> {
  try {
    // 1. Find Form Configuration linked to this form
    const configs = await formConfigsApi.findByFormId(formId);
    if (configs.length === 0) return null;
    const config = configs[0];
    const templateId = extractLookupId(config.Email_Template);
    if (!templateId) return null;
    // 2. Fetch the Email Template
    const template = await emailTemplatesApi.get(templateId);
    if (!template) return null;
    // 3. Find Newsletter linked to this form
    let newsletterId = '';
    try {
      const allNewsletters = await newslettersApi.list();
      const newsletter = allNewsletters.find(nl => {
        const nlFormId = extractLookupId(nl.Sign_Up_Form);
        return nlFormId === formId;
      });
      if (newsletter) {
        newsletterId = newsletter.Newsletter_ID || '';
      }
    } catch {
      // Newsletter lookup is optional — don't block
    }
    // 4. Extract confirmation/redirect URL from template
    let redirectUrl = '';
    const rawLink = (template as any).Confirmation_Link;
    if (rawLink) {
      if (typeof rawLink === 'string') {
        redirectUrl = rawLink;
      } else if (typeof rawLink === 'object' && rawLink.url) {
        redirectUrl = rawLink.url;
      }
    }
    return {
      emailHtml: template.Email_HTML_Body || '',
      emailSubject: template.Email_Subject || '',
      newsletterId,
      region: config.Region || template.Region || '',
      redirectUrl,
      appOrigin: window.location.origin
    };
  } catch (err) {
    console.error('Failed to fetch enrichment data:', err);
    return null;
  }
}
const DEFAULT_SUCCESS_MESSAGE = 'Successfully submitted! Please check your email to confirm.';
const DEFAULT_SUBMITTING_TEXT = 'Enviando…';
const DEFAULT_ERROR_MESSAGE = 'Error al enviar. Inténtalo de nuevo.';
const DEFAULT_VALIDATION_ERROR = 'Por favor, complete todos los campos obligatorios.';
/**
 * Generate the self-contained submission handler script.
 */
function generateSubmissionScript(team: string, slug: string, enrichment?: EnrichmentData | null, successMessage?: string, submittingText?: string, errorMessage?: string, validationErrorMessage?: string): string {
  var safeTeam = team.replace(/'/g, "\\'").replace(/\\/g, '\\\\');
  var safeSlug = slug.replace(/'/g, "\\'").replace(/\\/g, '\\\\');
  var safeSuccessMsg = escapeForJSString(successMessage || DEFAULT_SUCCESS_MESSAGE);
  var safeSubmittingText = escapeForJSString(submittingText || DEFAULT_SUBMITTING_TEXT);
  var safeErrorMsg = escapeForJSString(errorMessage || DEFAULT_ERROR_MESSAGE);
  var safeValidationMsg = escapeForJSString(validationErrorMessage || DEFAULT_VALIDATION_ERROR);
  // Pre-escape enrichment data for embedding in JS strings
  var safeEmailHtml = enrichment ? escapeForJSString(enrichment.emailHtml) : '';
  var safeEmailSubject = enrichment ? escapeForJSString(enrichment.emailSubject) : '';
  var safeNewsletterId = enrichment ? escapeForJSString(enrichment.newsletterId) : '';
  var safeRegion = enrichment ? escapeForJSString(enrichment.region) : '';
  var safeRedirectUrl = enrichment ? escapeForJSString(enrichment.redirectUrl) : '';
  var safeAppOrigin = enrichment ? escapeForJSString(enrichment.appOrigin) : '';
  var lines: string[] = [];
  lines.push('(function(){');
  lines.push("  var form=document.querySelector('form');");
  lines.push('  if(!form)return;');
  // Toast notification function — top center
  lines.push('  function showToast(msg,isErr){');
  lines.push("    var t=document.getElementById('_ft52');if(t)t.remove();");
  lines.push("    var s=document.createElement('style');");
  lines.push("    s.textContent='@keyframes _fti{from{transform:translate(-50%,-16px);opacity:0}to{transform:translate(-50%,0);opacity:1}}@keyframes _fto{from{opacity:1}to{opacity:0;transform:translate(-50%,-16px)}}';");
  lines.push('    document.head.appendChild(s);');
  lines.push("    t=document.createElement('div');t.id='_ft52';");
  lines.push("    t.style.cssText='position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:2147483647;background:'+(isErr?'#dc2626':'#16a34a')+';color:#fff;padding:14px 18px;border-radius:10px;font-size:14px;font-weight:500;box-shadow:0 8px 24px rgba(0,0,0,0.18);max-width:380px;display:flex;align-items:center;gap:12px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;animation:_fti 0.3s ease';");
  lines.push("    var sp=document.createElement('span');sp.textContent=msg;");
  lines.push("    var cl=document.createElement('button');cl.innerHTML='&times;';");
  lines.push("    cl.style.cssText='background:none;border:none;color:rgba(255,255,255,0.8);font-size:20px;cursor:pointer;padding:0;line-height:1;margin-left:4px;flex-shrink:0';");
  lines.push("    function dismiss(){t.style.animation='_fto 0.3s ease forwards';setTimeout(function(){if(t&&t.parentNode)t.remove()},300)}");
  lines.push('    cl.onclick=dismiss;t.appendChild(sp);t.appendChild(cl);document.body.appendChild(t);');
  lines.push('    setTimeout(dismiss,5000);');
  lines.push('  }');
  // Helper: generate a random confirmation token
  lines.push('  function genToken(){');
  lines.push('    var ts=Date.now().toString(36);');
  lines.push("    var r='';for(var i=0;i<16;i++)r+=Math.floor(Math.random()*36).toString(36);");
  lines.push("    return ts+'_'+r;");
  lines.push('  }');
  // Helper: replace {{variable}} placeholders in a template string
  lines.push('  function renderTpl(tpl,vars){');
  lines.push('    var result=tpl;');
  lines.push('    for(var k in vars){');
  lines.push('      if(!vars.hasOwnProperty(k))continue;');
  lines.push("      var re=new RegExp('\\\\{\\\\{\\\\s*'+k.replace(/[.*+?^${}()|[\\]\\\\]/g,'\\\\$&')+'\\\\s*\\\\}\\\\}','gi');");
  lines.push("      result=result.replace(re,vars[k]||'');");
  lines.push('    }');
  lines.push('    return result;');
  lines.push('  }');
  // Validation helper
  lines.push('  function validateForm(){');
  lines.push("    var reqFields=form.querySelectorAll('[required]');");
  lines.push('    for(var i=0;i<reqFields.length;i++){');
  lines.push('      var el=reqFields[i];');
  lines.push("      if(el.type==='checkbox'){if(!el.checked)return false;}");
  lines.push("      else if(el.type==='radio'){");
  lines.push('        var name=el.name;');
  lines.push("        var group=form.querySelectorAll('input[name=\"'+name+'\"]');");
  lines.push('        var anyChecked=false;');
  lines.push('        for(var j=0;j<group.length;j++){if(group[j].checked){anyChecked=true;break;}}');
  lines.push('        if(!anyChecked)return false;');
  lines.push('      }');
  lines.push('      else{if(!el.value||!String(el.value).trim())return false;}');
  lines.push('    }');
  lines.push('    return true;');
  lines.push('  }');
  // Intercept submit
  lines.push("  form.addEventListener('submit',function(e){");
  lines.push('    e.preventDefault();');
  lines.push('    e.stopImmediatePropagation();');
  // Validate required fields
  lines.push("    if(!validateForm()){showToast('" + safeValidationMsg + "',true);return;}");
  // Collect all form fields
  lines.push('    var fd=new FormData(form);');
  lines.push('    var fields={};');
  lines.push('    fd.forEach(function(v,k){fields[k]=v;});');
  // Fallback: manually collect from inputs
  lines.push('    if(Object.keys(fields).length===0){');
  lines.push("      var inputs=form.querySelectorAll('input,select,textarea');");
  lines.push('      for(var i=0;i<inputs.length;i++){');
  lines.push('        var el=inputs[i];');
  lines.push("        var n=el.getAttribute('name')||el.getAttribute('id')||'';");
  lines.push("        if(n&&el.type!=='submit'&&el.type!=='button'){");
  lines.push("          if(el.type==='checkbox')fields[n]=el.checked?'yes':'no';");
  lines.push("          else if(el.type==='radio'){if(el.checked)fields[n]=el.value;}");
  lines.push("          else fields[n]=el.value||el.textContent||'';");
  lines.push('        }');
  lines.push('      }');
  lines.push('    }');
  // Disable button with configurable text
  lines.push('    var btn=form.querySelector(\'[type="submit"],button:not([type])\');');
  lines.push('    var origText="";');
  lines.push('    if(btn){origText=btn.textContent;btn.disabled=true;btn.textContent="' + safeSubmittingText + '";}');
  // Determine subscriber email
  lines.push("    var email=fields.email||fields.Email||fields.email_address||fields.EMAIL||fields.emailAddress||fields.e_mail||'';");
  // Generate confirmation token
  lines.push('    var token=genToken();');
  // Build confirmation URL
  if (enrichment && enrichment.appOrigin) {
    lines.push("    var confirmUrl='" + safeAppOrigin + "/confirm?token='+encodeURIComponent(token);");
  } else {
    lines.push("    var confirmUrl=window.location.origin+'/confirm?token='+encodeURIComponent(token);");
  }
  // Render email template if enrichment data is available
  if (enrichment && enrichment.emailHtml) {
    lines.push("    var tplHtml='" + safeEmailHtml + "';");
    lines.push("    var tplSubject='" + safeEmailSubject + "';");
    lines.push('    var tplVars={};');
    lines.push('    for(var fk in fields){if(fields.hasOwnProperty(fk))tplVars[fk]=fields[fk];}');
    lines.push('    tplVars.confirmation_link=confirmUrl;');
    lines.push("    tplVars.team_name='" + safeTeam + "';");
    lines.push('    var renderedHtml=renderTpl(tplHtml,tplVars);');
    lines.push('    var renderedSubject=renderTpl(tplSubject,tplVars);');
  } else {
    lines.push("    var renderedHtml='';");
    lines.push("    var renderedSubject='';");
  }
  // Build the full submission payload
  lines.push('    var payload={');
  lines.push('      Form_Payload:JSON.stringify(fields),');
  lines.push("      Status:'Not Subscribed',");
  lines.push("      Team:'" + safeTeam + "',");
  lines.push('      Subscriber_Email:email,');
  lines.push('      Confirmation_Token:token,');
  if (enrichment && enrichment.newsletterId) {
    lines.push("      Newsletter_ID:'" + safeNewsletterId + "',");
  }
  if (enrichment && enrichment.region) {
    lines.push("      Region:'" + safeRegion + "',");
  }
  if (enrichment && enrichment.redirectUrl) {
    lines.push("      Redirect_URL:'" + safeRedirectUrl + "',");
  }
  lines.push('      Rendered_Email_HTML:renderedHtml,');
  lines.push('      Rendered_Email_Subject:renderedSubject');
  lines.push('    };');
  // POST to the API proxy
  lines.push("    fetch('https://www.websiteforge.com/custom/chosen_zoho_api.php',{");
  lines.push("      method:'POST',");
  lines.push("      headers:{'Content-Type':'application/json','API-KEY':'ud7^3jf(80'},");
  lines.push('      body:JSON.stringify({');
  lines.push("        url:'https://www.zohoapis.com/creator/v2.1/data/52studios/campaigns/form/Submissions',");
  lines.push("        method:'POST',");
  lines.push('        data:{data:payload}');
  lines.push('      })');
  lines.push('    })');
  lines.push('    .then(function(r){return r.json();})');
  lines.push('    .then(function(data){');
  lines.push("      showToast('" + safeSuccessMsg + "',false);");
  lines.push('      if(btn){btn.disabled=false;btn.textContent=origText;}');
  lines.push('      form.reset();');
  lines.push('    })');
  lines.push('    .catch(function(err){');
  lines.push("      showToast('" + safeErrorMsg + "',true);");
  lines.push('      if(btn){btn.disabled=false;btn.textContent=origText;}');
  lines.push("      console.error('Form submission error:',err);");
  lines.push('    });');
  lines.push('  },true);');
  lines.push('})();');
  return lines.join('\n');
}
/** Generate a self-contained JS embed snippet */
function generateJsEmbed(slug: string, html: string, css: string, js: string, team: string, enrichment?: EnrichmentData | null, successMessage?: string, submittingText?: string, errorMessage?: string, validationErrorMessage?: string): string {
  if (!html.trim()) return '';
  var containerId = 'form-52s-' + (slug || 'preview');
  var safeHtml = escapeForJSString(html);
  var safeCss = escapeForJSString(css);
  var safeJs = js.replace(/<\/script>/gi, '<\\/script>');
  var submissionScript = generateSubmissionScript(team, slug, enrichment, successMessage, submittingText, errorMessage, validationErrorMessage);
  var safeSubmission = submissionScript.replace(/<\/script>/gi, '<\\/script>');
  var lines: string[] = [];
  lines.push('<div id="' + containerId + '"></div>');
  lines.push('<script>');
  lines.push('(function(){');
  lines.push("  var c=document.getElementById('" + containerId + "');");
  lines.push('  if(!c)return;');
  lines.push("  c.innerHTML='<sty" + 'le>' + safeCss + '</sty' + 'le>' + safeHtml + "';");
  lines.push('  var sh=document.createElement("script");');
  lines.push('  sh.textContent=' + JSON.stringify(safeSubmission) + ';');
  lines.push('  c.appendChild(sh);');
  if (safeJs.trim()) {
    lines.push('  var us=document.createElement("script");');
    lines.push('  us.textContent=' + JSON.stringify(safeJs) + ';');
    lines.push('  c.appendChild(us);');
  }
  lines.push('})();');
  lines.push('<\/script>');
  return lines.join('\n');
}
/** Generate a self-contained iframe embed using srcdoc */
function generateIframeEmbed(slug: string, html: string, css: string, js: string, team: string, enrichment?: EnrichmentData | null, successMessage?: string, submittingText?: string, errorMessage?: string, validationErrorMessage?: string): string {
  if (!html.trim()) return '';
  var resetCss = '*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;padding:1rem}';
  var fullCss = resetCss + (css || '');
  var submissionScript = generateSubmissionScript(team, slug, enrichment, successMessage, submittingText, errorMessage, validationErrorMessage);
  var docParts: string[] = [];
  docParts.push('<!DOCTYPE html><html><head><meta charset="utf-8">');
  docParts.push('<meta name="viewport" content="width=device-width,initial-scale=1">');
  docParts.push('<sty' + 'le>' + fullCss + '</sty' + 'le>');
  docParts.push('</head><body>');
  docParts.push(html);
  docParts.push('<script>' + submissionScript + '<\/script>');
  if (js && js.trim()) {
    docParts.push('<script>' + js + '<\/script>');
  }
  docParts.push('</body></html>');
  var doc = docParts.join('');
  var srcdoc = doc.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return '<iframe srcdoc="' + srcdoc + '" width="100%" height="500" frameborder="0" style="border:none;overflow:hidden;" allowtransparency="true"></iframe>';
}
/** Extract form field names from HTML */
function extractFormFields(html: string): string[] {
  const fields: string[] = [];
  const regex = /(?:name|id)=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const name = match[1];
    if (name && !fields.includes(name) && !['submit', 'button'].includes(name.toLowerCase())) {
      fields.push(name);
    }
  }
  return fields;
}
/** Check if form HTML changes affect linked email templates */
async function checkVariableImpact(formId: string, oldHtml: string, newHtml: string): Promise<{
  affected: boolean;
  removedFields: string[];
  templateSubjects: string[];
}> {
  const oldFields = extractFormFields(oldHtml);
  const newFields = extractFormFields(newHtml);
  const removedFields = oldFields.filter(f => !newFields.includes(f));
  if (removedFields.length === 0) {
    return {
      affected: false,
      removedFields: [],
      templateSubjects: []
    };
  }
  const configs = await formConfigsApi.findByFormId(formId);
  if (configs.length === 0) {
    return {
      affected: false,
      removedFields,
      templateSubjects: []
    };
  }
  const templateSubjects: string[] = [];
  for (const config of configs) {
    const templateId = extractLookupId(config.Email_Template);
    if (!templateId) continue;
    try {
      const template = await emailTemplatesApi.get(templateId);
      if (!template) continue;
      const html = template.Email_HTML_Body || '';
      const usesRemoved = removedFields.some(f => html.includes('{{' + f + '}}'));
      if (usesRemoved) {
        templateSubjects.push(template.Email_Subject || 'Template ' + templateId);
      }
    } catch {
      // Skip templates we can't fetch
    }
  }
  return {
    affected: templateSubjects.length > 0,
    removedFields,
    templateSubjects
  };
}
export function SignupFormEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js'>('html');
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [builderBlocks, setBuilderBlocks] = useState<FormBlock[]>([]);
  const [builderTheme, setBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [formName, setFormName] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [status, setStatus] = useState('active');
  const [formData, setFormData] = useState({
    html: '',
    css: '',
    js: ''
  });
  const [originalHtml, setOriginalHtml] = useState('');
  const [enrichmentData, setEnrichmentData] = useState<EnrichmentData | null>(null);
  const [isLoadingEnrichment, setIsLoadingEnrichment] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState(DEFAULT_SUCCESS_MESSAGE);
  const [submittingText, setSubmittingText] = useState(DEFAULT_SUBMITTING_TEXT);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const [validationErrorMessage, setValidationErrorMessage] = useState(DEFAULT_VALIDATION_ERROR);
  // Template picker state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templatePickerTemplates, setTemplatePickerTemplates] = useState<FormTemplateRecord[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateApplied, setTemplateApplied] = useState(false);
  const handleCopyEmbed = (type: string, text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedEmbed(type);
      toast.success('Embed code copied to clipboard');
      setTimeout(() => setCopiedEmbed(null), 2000);
    }).catch(() => {
      // Fallback for non-HTTPS contexts
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      setCopiedEmbed(type);
      toast.success('Embed code copied to clipboard');
      setTimeout(() => setCopiedEmbed(null), 2000);
    });
  };
  useEffect(() => {
    if (!slugManuallyEdited && !isEditing) {
      setUrlSlug(toSlug(formName));
    }
  }, [formName, slugManuallyEdited, isEditing]);
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      signupFormsApi.get(id).then(async record => {
        if (record) {
          setFormName(record.Form_Name || '');
          setUrlSlug(record.Url_Slug || '');
          setSlugManuallyEdited(true);
          setStatus(record.Status || 'active');
          setFormData({
            html: record.HTML || '',
            css: record.CSS || '',
            js: record.JavaScript1 || ''
          });
          setOriginalHtml(record.HTML || '');
          // Restore builder state and settings from Builder_Schema
          if (record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema & {
                successMessage?: string;
                submittingText?: string;
                errorMessage?: string;
                validationErrorMessage?: string;
              } = JSON.parse(record.Builder_Schema);
              // Restore success message
              if (schema.successMessage) {
                setSuccessMessage(schema.successMessage);
              }
              if (schema.submittingText) {
                setSubmittingText(schema.submittingText);
              }
              if (schema.errorMessage) {
                setErrorMessage(schema.errorMessage);
              }
              if (schema.validationErrorMessage) {
                setValidationErrorMessage(schema.validationErrorMessage);
              }
              if (record.Builder_Mode === 'builder' && schema.blocks) {
                setBuilderBlocks(schema.blocks || []);
                setBuilderTheme({
                  ...DEFAULT_FORM_THEME,
                  ...(schema.theme || {})
                });
                setEditorMode('builder');
              } else if (schema.blocks) {
                setBuilderBlocks(schema.blocks || []);
                setBuilderTheme({
                  ...DEFAULT_FORM_THEME,
                  ...(schema.theme || {})
                });
              }
            } catch {
              /* ignore parse errors */
            }
          }
          // Fetch linked email template + newsletter data
          setIsLoadingEnrichment(true);
          try {
            const enrichment = await fetchEnrichmentData(id);
            setEnrichmentData(enrichment);
            if (enrichment) {
              toast.info('Linked email template loaded', {
                description: enrichment.emailSubject ? `Template: "${enrichment.emailSubject}"` : 'Template data will be included in embed codes.',
                duration: 3000
              });
            }
          } catch (err) {
            console.error('Failed to load enrichment data:', err);
          } finally {
            setIsLoadingEnrichment(false);
          }
        } else {
          toast.error('Form not found');
          navigate('/forms');
        }
      }).catch(err => {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Failed to load form', {
          description: msg
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const openTemplatePicker = async () => {
    setShowTemplatePicker(true);
    setIsLoadingTemplates(true);
    try {
      const all = await formTemplatesLibraryApi.list();
      // Filter by team: show templates belonging to current workspace or with no team (shared)
      const teamFiltered = all.filter(t => !t.Team || t.Team === currentWorkspace.name.replace(/\s*Team\s*$/i, ''));
      setTemplatePickerTemplates(teamFiltered);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setIsLoadingTemplates(false);
    }
  };
  const applyFormTemplate = (tpl: FormTemplateRecord) => {
    setFormData({
      html: tpl.HTML || '',
      css: tpl.CSS || '',
      js: tpl.JavaScript1 || ''
    });
    if (tpl.Builder_Mode === 'builder' && tpl.Builder_Schema) {
      try {
        const schema: FormBuilderSchema = JSON.parse(tpl.Builder_Schema);
        setBuilderBlocks(schema.blocks || []);
        setBuilderTheme({
          ...DEFAULT_FORM_THEME,
          ...(schema.theme || {})
        });
        setEditorMode('builder');
      } catch {
        /* fallback to code */
      }
    } else if (tpl.Builder_Schema) {
      try {
        const schema: FormBuilderSchema = JSON.parse(tpl.Builder_Schema);
        setBuilderBlocks(schema.blocks || []);
        setBuilderTheme({
          ...DEFAULT_FORM_THEME,
          ...(schema.theme || {})
        });
      } catch {
        /* ignore */
      }
    }
    setTemplateApplied(true);
    setShowTemplatePicker(false);
    toast.success(`Template "${tpl.Template_Name}" applied`, {
      description: 'Set a form name and save when ready.',
      duration: 3000
    });
  };
  const filteredPickerTemplates = templatePickerTemplates.filter(t => {
    if (!templateSearch) return true;
    const q = templateSearch.toLowerCase();
    return (t.Template_Name || '').toLowerCase().includes(q) || (t.Description || '').toLowerCase().includes(q) || (t.Category || '').toLowerCase().includes(q);
  });
  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Form Name is required');
      return;
    }
    setIsSaving(true);
    // Use current enrichment data for embed generation
    let currentEnrichment = enrichmentData;
    const jsEmbed = generateJsEmbed(urlSlug, formData.html, formData.css, formData.js, team, currentEnrichment, successMessage, submittingText, errorMessage, validationErrorMessage);
    const iframeEmbed = generateIframeEmbed(urlSlug, formData.html, formData.css, formData.js, team, currentEnrichment, successMessage, submittingText, errorMessage, validationErrorMessage);
    const payload: Record<string, any> = {
      Form_Name: formName,
      Url_Slug: urlSlug,
      Status: status,
      HTML: formData.html,
      CSS: formData.css,
      JavaScript1: formData.js,
      JavaScript_Embed: jsEmbed,
      Iframe_Embed: iframeEmbed,
      Team: team,
      Builder_Mode: editorMode
    };
    // Always save Builder_Schema to persist successMessage and builder state
    const schemaData: Record<string, any> = {
      successMessage,
      submittingText,
      errorMessage,
      validationErrorMessage
    };
    if (builderBlocks.length > 0 || editorMode === 'builder') {
      schemaData.blocks = builderBlocks;
      schemaData.theme = builderTheme;
    }
    payload.Builder_Schema = JSON.stringify(schemaData);
    try {
      let savedFormId = id || '';
      if (isEditing && id) {
        await signupFormsApi.update(id, payload);
        savedFormId = id;
        toast.success('Signup form updated', {
          description: '"' + formName + '" has been updated.'
        });
        if (originalHtml && formData.html !== originalHtml) {
          try {
            const impact = await checkVariableImpact(id, originalHtml, formData.html);
            if (impact.affected) {
              toast.warning('Form variables changed — templates may need updating', {
                description: 'Removed fields (' + impact.removedFields.join(', ') + ') are used in: ' + impact.templateSubjects.join(', '),
                duration: 10000,
                action: {
                  label: 'View Configurations',
                  onClick: () => navigate('/configurations')
                }
              });
            } else if (impact.removedFields.length > 0) {
              toast.info('Form fields changed', {
                description: 'Removed: ' + impact.removedFields.join(', ') + '. No linked templates affected.',
                duration: 5000
              });
            }
          } catch (err) {
            console.error('Variable impact check failed:', err);
          }
          setOriginalHtml(formData.html);
        }
      } else {
        const result = await signupFormsApi.create(payload);
        if (result?.data?.ID) {
          savedFormId = String(result.data.ID);
        }
        toast.success('Signup form created', {
          description: '"' + formName + '" has been saved to Zoho Creator.'
        });
      }
      // After saving, try to fetch enrichment data and re-save embed codes if available
      if (savedFormId) {
        try {
          const freshEnrichment = await fetchEnrichmentData(savedFormId);
          if (freshEnrichment) {
            setEnrichmentData(freshEnrichment);
            if (!currentEnrichment || JSON.stringify(freshEnrichment) !== JSON.stringify(currentEnrichment)) {
              const updatedJsEmbed = generateJsEmbed(urlSlug, formData.html, formData.css, formData.js, team, freshEnrichment, successMessage, submittingText, errorMessage, validationErrorMessage);
              const updatedIframeEmbed = generateIframeEmbed(urlSlug, formData.html, formData.css, formData.js, team, freshEnrichment, successMessage, submittingText, errorMessage, validationErrorMessage);
              await signupFormsApi.update(savedFormId, {
                JavaScript_Embed: updatedJsEmbed,
                Iframe_Embed: updatedIframeEmbed
              });
              toast.success('Embed codes enriched with email template data', {
                description: freshEnrichment.emailSubject ? `Linked template: "${freshEnrichment.emailSubject}"` : 'Template data baked into embed codes.',
                duration: 4000
              });
            }
          } else if (!currentEnrichment) {
            toast.info('No email template linked', {
              description: 'Link an email template via Form Configurations to enable confirmation emails in the embed.',
              duration: 5000
            });
          }
        } catch (err) {
          console.error('Post-save enrichment failed:', err);
        }
      }
      if (!isEditing) {
        navigate('/forms');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save signup form', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4180">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4181" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4182">Loading form...</span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4183">
      <div className="flex items-center justify-between" data-id="element-4184">
        <div className="flex items-center gap-4" data-id="element-4185">
          <Link to="/forms" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-4186">
            <ArrowLeft className="h-5 w-5" data-id="element-4187" />
          </Link>
          <div data-id="element-4188">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4189">
              {isEditing ? 'Edit Form: ' + (formName || '...') : 'Create New Form'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-4190">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your form.' : 'Configure the raw HTML, CSS, and JS for this form.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-4191">
          {/* Embed Code Link — only for saved forms */}
          {isEditing && id && <Link to={`/forms/${id}/embed`} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4192">
              <Code className="h-4 w-4" data-id="element-4193" /> Embed Code
            </Link>}
          {/* Mode Toggle */}
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-4194">
            <button onClick={() => {
            if (editorMode === 'builder' && builderBlocks.length > 0) {
              // Builder → Code: HTML/CSS already synced
            }
            setEditorMode('code');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4195">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && formData.html && builderBlocks.length === 0) {
              if (!confirm('Switching to Builder mode will start fresh. Manual HTML edits cannot be imported into the builder. Continue?')) return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4196">
              Builder
            </button>
          </div>
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4197">
            <Eye className="h-4 w-4" data-id="element-4198" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-4199">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4200" /> : <Save className="h-4 w-4" data-id="element-4201" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* Template Picker Banner — only for new forms, before a template is applied */}
      {!isEditing && !templateApplied && !formData.html && <div className="bg-white border border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-between" data-id="element-4202">
          <div className="flex items-center gap-3" data-id="element-4203">
            <div className="p-2 bg-indigo-50 rounded-lg" data-id="element-4204">
              <LayoutTemplateIcon className="h-5 w-5 text-indigo-600" data-id="element-4205" />
            </div>
            <div data-id="element-4206">
              <p className="text-sm font-medium text-gray-900" data-id="element-4207">
                Start from a template
              </p>
              <p className="text-xs text-gray-500" data-id="element-4208">
                Pre-fill this form with a saved template from your library.
              </p>
            </div>
          </div>
          <button onClick={openTemplatePicker} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 whitespace-nowrap" data-id="element-4209">
            Browse Templates
          </button>
        </div>}

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-4210">
          <FormBuilder initialBlocks={builderBlocks} initialTheme={builderTheme} onSchemaChange={(blocks, theme) => {
        setBuilderBlocks(blocks);
        setBuilderTheme(theme);
      }} onHtmlChange={(html, css) => {
        setFormData(prev => ({
          ...prev,
          html,
          css
        }));
      }} data-id="element-4211" />
          {/* Settings sidebar below builder in builder mode */}
          <div className="grid grid-cols-3 gap-6" data-id="element-4212">
            <div className="col-span-2" data-id="element-4213" />
            <div className="space-y-6" data-id="element-4214">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4215">
                <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4216">
                  Settings
                </h3>
                <div className="space-y-2" data-id="element-4217">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4218">
                    Form Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formName} onChange={e => setFormName(e.target.value)} placeholder="My Signup Form" data-id="element-4219" />
                </div>
                <div className="space-y-2" data-id="element-4220">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4221">
                    URL Slug
                  </label>
                  <div className="flex rounded-md shadow-sm" data-id="element-4222">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-4223">
                      /forms/
                    </span>
                    <input type="text" className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={urlSlug} onChange={e => {
                  setUrlSlug(e.target.value);
                  setSlugManuallyEdited(true);
                }} data-id="element-4224" />
                  </div>
                </div>
                <div className="space-y-2" data-id="element-4225">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4226">
                    Status
                  </label>
                  <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={status} onChange={e => setStatus(e.target.value)} data-id="element-4227">
                    <option value="active" data-id="element-4228">Active</option>
                    <option value="draft" data-id="element-4229">Draft</option>
                    <option value="archived" data-id="element-4230">Archived</option>
                  </select>
                </div>
                <div className="space-y-2" data-id="element-4231">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4232">
                    Team
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4233" />
                </div>
                <div className="space-y-2" data-id="element-4234">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4235">
                    Success Message
                  </label>
                  <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={successMessage} onChange={e => setSuccessMessage(e.target.value)} placeholder="Successfully submitted! Please check your email to confirm." data-id="element-4236" />
                  <p className="text-xs text-gray-400" data-id="element-4237">
                    Shown as a popup toast after submission. Save to apply.
                  </p>
                </div>
                <div className="space-y-2" data-id="element-4238">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4239">
                    Submitting Button Text
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={submittingText} onChange={e => setSubmittingText(e.target.value)} placeholder="Enviando…" data-id="element-4240" />
                  <p className="text-xs text-gray-400" data-id="element-4241">
                    Button text shown while the form is being submitted.
                  </p>
                </div>
                <div className="space-y-2" data-id="element-4242">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4243">
                    Error Message
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={errorMessage} onChange={e => setErrorMessage(e.target.value)} placeholder="Error al enviar. Inténtalo de nuevo." data-id="element-4244" />
                  <p className="text-xs text-gray-400" data-id="element-4245">
                    Shown when the submission fails (API error).
                  </p>
                </div>
                <div className="space-y-2" data-id="element-4246">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4247">
                    Validation Error Message
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={validationErrorMessage} onChange={e => setValidationErrorMessage(e.target.value)} placeholder="Por favor, complete todos los campos obligatorios." data-id="element-4248" />
                  <p className="text-xs text-gray-400" data-id="element-4249">
                    Shown when required fields are left blank.
                  </p>
                </div>
              </div>

              {/* Enrichment Status Card — same as code mode */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-4250">
                <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4251">
                  Linked Email Template
                </h3>
                {isLoadingEnrichment ? <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-4252">
                    <Loader2 className="h-4 w-4 animate-spin" data-id="element-4253" />
                    Loading linked template...
                  </div> : enrichmentData ? <div className="space-y-2" data-id="element-4254">
                    <div className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded-md" data-id="element-4255">
                      <svg className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4256">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-4257" />
                      </svg>
                      <div className="text-xs text-green-800" data-id="element-4258">
                        <p className="font-medium" data-id="element-4259">
                          Template linked &amp; baked into embeds
                        </p>
                        {enrichmentData.emailSubject && <p className="mt-0.5 text-green-700" data-id="element-4260">
                            Subject: {enrichmentData.emailSubject}
                          </p>}
                        {enrichmentData.newsletterId && <p className="text-green-700" data-id="element-4261">
                            Newsletter ID: {enrichmentData.newsletterId}
                          </p>}
                        {enrichmentData.redirectUrl && <p className="text-green-700" data-id="element-4262">
                            Redirect: {enrichmentData.redirectUrl}
                          </p>}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-4263">
                      Embed codes include confirmation token generation, email
                      template rendering, and all submission fields.
                    </p>
                  </div> : <div className="space-y-2" data-id="element-4264">
                    <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-id="element-4265">
                      <svg className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4266">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-4267" />
                      </svg>
                      <div className="text-xs text-amber-800" data-id="element-4268">
                        <p className="font-medium" data-id="element-4269">No email template linked</p>
                        <p className="mt-0.5" data-id="element-4270">
                          Submissions will be created with basic data only (no
                          confirmation email, no rendered HTML).
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-4271">
                      To enable full submissions: create a Form Configuration
                      linking this form to an email template, then re-save this
                      form.
                    </p>
                  </div>}
              </div>

              {/* Embed Codes Card — now available in builder mode too */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4272">
                <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4273">
                  Embed Codes
                </h3>
                <p className="text-xs text-gray-500" data-id="element-4274">
                  Self-contained embed codes with built-in submission handling.
                  They update live as you edit.
                  {enrichmentData && <span className="text-green-600 font-medium" data-id="element-4275">
                      {' '}
                      ✓ Includes email template data.
                    </span>}
                </p>
                <div className="space-y-2" data-id="element-4276">
                  <div className="flex items-center justify-between" data-id="element-4277">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-4278">
                      Iframe Embed
                    </label>
                    <button onClick={() => handleCopyEmbed('builder-iframe', generateIframeEmbed(urlSlug, formData.html, formData.css, formData.js, team, enrichmentData, successMessage, submittingText, errorMessage, validationErrorMessage))} disabled={!formData.html.trim()} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50" data-id="element-4279">
                      {copiedEmbed === 'builder-iframe' ? <Check className="h-3.5 w-3.5" data-id="element-4280" /> : <Copy className="h-3.5 w-3.5" data-id="element-4281" />}
                      {copiedEmbed === 'builder-iframe' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <textarea rows={4} className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm font-mono text-xs text-gray-500" value={generateIframeEmbed(urlSlug, formData.html, formData.css, formData.js, team, enrichmentData, successMessage, submittingText, errorMessage, validationErrorMessage)} readOnly onClick={e => (e.target as HTMLTextAreaElement).select()} placeholder="Add form blocks to generate embed code" data-id="element-4282" />
                </div>
                <div className="space-y-2" data-id="element-4283">
                  <div className="flex items-center justify-between" data-id="element-4284">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-4285">
                      JavaScript Embed
                    </label>
                    <button onClick={() => handleCopyEmbed('builder-script', generateJsEmbed(urlSlug, formData.html, formData.css, formData.js, team, enrichmentData, successMessage, submittingText, errorMessage, validationErrorMessage))} disabled={!formData.html.trim()} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50" data-id="element-4286">
                      {copiedEmbed === 'builder-script' ? <Check className="h-3.5 w-3.5" data-id="element-4287" /> : <Copy className="h-3.5 w-3.5" data-id="element-4288" />}
                      {copiedEmbed === 'builder-script' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <textarea rows={4} className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm font-mono text-xs text-gray-500" value={generateJsEmbed(urlSlug, formData.html, formData.css, formData.js, team, enrichmentData, successMessage, submittingText, errorMessage, validationErrorMessage)} readOnly onClick={e => (e.target as HTMLTextAreaElement).select()} placeholder="Add form blocks to generate embed code" data-id="element-4289" />
                </div>
                {isEditing && id && <Link to={`/forms/${id}/embed`} className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-4290">
                    <Code className="h-3.5 w-3.5" data-id="element-4291" />
                    View full embed page →
                  </Link>}
              </div>
            </div>
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-4292">
          <div className="col-span-2 space-y-6" data-id="element-4293">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-4294">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-4" data-id="element-4295">
                {(['html', 'css', 'js'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={'text-sm font-medium px-3 py-1.5 rounded-md transition-colors ' + (activeTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50')} data-id="element-4296">
                    {tab === 'js' ? 'JavaScript' : tab.toUpperCase()}
                  </button>)}
              </div>
              <div className="p-4" data-id="element-4297">
                {activeTab === 'html' && <CodeEditor label="Form HTML" language="html" placeholder="<form>...</form>" value={formData.html} onChange={e => setFormData({
              ...formData,
              html: e.target.value
            })} data-id="element-4298" />}
                {activeTab === 'css' && <CodeEditor label="Custom CSS" language="css" placeholder=".signup-form { ... }" value={formData.css} onChange={e => setFormData({
              ...formData,
              css: e.target.value
            })} data-id="element-4299" />}
                {activeTab === 'js' && <CodeEditor label="Custom JavaScript" language="javascript" placeholder="document.querySelector..." value={formData.js} onChange={e => setFormData({
              ...formData,
              js: e.target.value
            })} data-id="element-4300" />}
              </div>
            </div>

            {/* GPT Code Assistant */}
            <GptCodeAssistant mode="form" currentCode={formData} onCodeGenerated={code => {
          setFormData(prev => ({
            html: code.html || prev.html,
            css: code.css !== undefined ? code.css : prev.css,
            js: code.js !== undefined ? code.js : prev.js
          }));
        }} data-id="element-4301" />
          </div>

          <div className="space-y-6" data-id="element-4302">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4303">
              <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4304">
                Settings
              </h3>
              <div className="space-y-2" data-id="element-4305">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4306">
                  Form Name
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formName} onChange={e => setFormName(e.target.value)} placeholder="My Signup Form" data-id="element-4307" />
              </div>
              <div className="space-y-2" data-id="element-4308">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4309">
                  URL Slug
                </label>
                <div className="flex rounded-md shadow-sm" data-id="element-4310">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-4311">
                    /forms/
                  </span>
                  <input type="text" className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={urlSlug} onChange={e => {
                setUrlSlug(e.target.value);
                setSlugManuallyEdited(true);
              }} placeholder="auto-generated-from-name" data-id="element-4312" />
                </div>
                {!slugManuallyEdited && !isEditing && formName && <p className="text-xs text-gray-400" data-id="element-4313">
                    Auto-generated from form name
                  </p>}
              </div>
              <div className="space-y-2" data-id="element-4314">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4315">
                  Status
                </label>
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={status} onChange={e => setStatus(e.target.value)} data-id="element-4316">
                  <option value="active" data-id="element-4317">Active</option>
                  <option value="draft" data-id="element-4318">Draft</option>
                  <option value="archived" data-id="element-4319">Archived</option>
                </select>
              </div>
              <div className="space-y-2" data-id="element-4320">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4321">
                  Team
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4322" />
                <p className="text-xs text-gray-400" data-id="element-4323">
                  Automatically set from your current workspace scope
                </p>
              </div>
              <div className="space-y-2" data-id="element-4324">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4325">
                  Success Message
                </label>
                <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={successMessage} onChange={e => setSuccessMessage(e.target.value)} placeholder="Successfully submitted! Please check your email to confirm." data-id="element-4326" />
                <p className="text-xs text-gray-400" data-id="element-4327">
                  Shown as a popup toast after the form is submitted. Save to
                  apply.
                </p>
              </div>
              <div className="space-y-2" data-id="element-4328">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4329">
                  Submitting Button Text
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={submittingText} onChange={e => setSubmittingText(e.target.value)} placeholder="Enviando…" data-id="element-4330" />
                <p className="text-xs text-gray-400" data-id="element-4331">
                  Button text shown while the form is being submitted.
                </p>
              </div>
              <div className="space-y-2" data-id="element-4332">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4333">
                  Error Message
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={errorMessage} onChange={e => setErrorMessage(e.target.value)} placeholder="Error al enviar. Inténtalo de nuevo." data-id="element-4334" />
                <p className="text-xs text-gray-400" data-id="element-4335">
                  Shown when the submission fails (API error).
                </p>
              </div>
              <div className="space-y-2" data-id="element-4336">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4337">
                  Validation Error Message
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={validationErrorMessage} onChange={e => setValidationErrorMessage(e.target.value)} placeholder="Por favor, complete todos los campos obligatorios." data-id="element-4338" />
                <p className="text-xs text-gray-400" data-id="element-4339">
                  Shown when required fields are left blank.
                </p>
              </div>
            </div>

            {/* Enrichment Status Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-4340">
              <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4341">
                Linked Email Template
              </h3>
              {isLoadingEnrichment ? <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-4342">
                  <Loader2 className="h-4 w-4 animate-spin" data-id="element-4343" />
                  Loading linked template...
                </div> : enrichmentData ? <div className="space-y-2" data-id="element-4344">
                  <div className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded-md" data-id="element-4345">
                    <svg className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4346">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-4347" />
                    </svg>
                    <div className="text-xs text-green-800" data-id="element-4348">
                      <p className="font-medium" data-id="element-4349">
                        Template linked & baked into embeds
                      </p>
                      {enrichmentData.emailSubject && <p className="mt-0.5 text-green-700" data-id="element-4350">
                          Subject: {enrichmentData.emailSubject}
                        </p>}
                      {enrichmentData.newsletterId && <p className="text-green-700" data-id="element-4351">
                          Newsletter ID: {enrichmentData.newsletterId}
                        </p>}
                      {enrichmentData.redirectUrl && <p className="text-green-700" data-id="element-4352">
                          Redirect: {enrichmentData.redirectUrl}
                        </p>}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-4353">
                    Embed codes include confirmation token generation, email
                    template rendering, and all submission fields.
                  </p>
                </div> : <div className="space-y-2" data-id="element-4354">
                  <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-id="element-4355">
                    <svg className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4356">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-4357" />
                    </svg>
                    <div className="text-xs text-amber-800" data-id="element-4358">
                      <p className="font-medium" data-id="element-4359">No email template linked</p>
                      <p className="mt-0.5" data-id="element-4360">
                        Submissions will be created with basic data only (no
                        confirmation email, no rendered HTML).
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-4361">
                    To enable full submissions: create a Form Configuration
                    linking this form to an email template, then re-save this
                    form.
                  </p>
                </div>}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4362">
              <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4363">
                Embed Codes
              </h3>
              <p className="text-xs text-gray-500" data-id="element-4364">
                Self-contained embed codes with built-in submission handling.
                They update live as you edit.
                {enrichmentData && <span className="text-green-600 font-medium" data-id="element-4365">
                    {' '}
                    ✓ Includes email template data.
                  </span>}
              </p>
              <div className="space-y-2" data-id="element-4366">
                <div className="flex items-center justify-between" data-id="element-4367">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4368">
                    Iframe Embed
                  </label>
                  <button onClick={() => handleCopyEmbed('code-iframe', generateIframeEmbed(urlSlug, formData.html, formData.css, formData.js, team, enrichmentData, successMessage, submittingText, errorMessage, validationErrorMessage))} disabled={!formData.html.trim()} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50" data-id="element-4369">
                    {copiedEmbed === 'code-iframe' ? <Check className="h-3.5 w-3.5" data-id="element-4370" /> : <Copy className="h-3.5 w-3.5" data-id="element-4371" />}
                    {copiedEmbed === 'code-iframe' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <textarea rows={4} className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm font-mono text-xs text-gray-500" value={generateIframeEmbed(urlSlug, formData.html, formData.css, formData.js, team, enrichmentData, successMessage, submittingText, errorMessage, validationErrorMessage)} readOnly onClick={e => (e.target as HTMLTextAreaElement).select()} placeholder="Add HTML to generate embed code" data-id="element-4372" />
              </div>
              <div className="space-y-2" data-id="element-4373">
                <div className="flex items-center justify-between" data-id="element-4374">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4375">
                    JavaScript Embed
                  </label>
                  <button onClick={() => handleCopyEmbed('code-script', generateJsEmbed(urlSlug, formData.html, formData.css, formData.js, team, enrichmentData, successMessage, submittingText, errorMessage, validationErrorMessage))} disabled={!formData.html.trim()} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50" data-id="element-4376">
                    {copiedEmbed === 'code-script' ? <Check className="h-3.5 w-3.5" data-id="element-4377" /> : <Copy className="h-3.5 w-3.5" data-id="element-4378" />}
                    {copiedEmbed === 'code-script' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <textarea rows={4} className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm font-mono text-xs text-gray-500" value={generateJsEmbed(urlSlug, formData.html, formData.css, formData.js, team, enrichmentData, successMessage, submittingText, errorMessage, validationErrorMessage)} readOnly onClick={e => (e.target as HTMLTextAreaElement).select()} placeholder="Add HTML to generate embed code" data-id="element-4379" />
              </div>
              {isEditing && id && <Link to={`/forms/${id}/embed`} className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-4380">
                  <Code className="h-3.5 w-3.5" data-id="element-4381" />
                  View full embed page →
                </Link>}
            </div>
          </div>
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Form Preview" data-id="element-4382">
        <FormPreview html={formData.html} css={formData.css} data-id="element-4383" />
      </PreviewModal>

      {/* Template Picker Modal */}
      {showTemplatePicker && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4384">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4385">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowTemplatePicker(false)} data-id="element-4386" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" data-id="element-4387">
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-4388">
                <div className="flex items-center justify-between" data-id="element-4389">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-4390">
                    Choose a Form Template
                  </h3>
                  <button onClick={() => setShowTemplatePicker(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none" data-id="element-4391">
                    &times;
                  </button>
                </div>
                <div className="mt-3 relative" data-id="element-4392">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4393" />
                  <input type="text" placeholder="Search templates..." value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500" autoFocus data-id="element-4394" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6" data-id="element-4395">
                {isLoadingTemplates ? <div className="flex items-center justify-center py-12" data-id="element-4396">
                    <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" data-id="element-4397" />
                    <span className="ml-2 text-sm text-gray-500" data-id="element-4398">
                      Loading templates...
                    </span>
                  </div> : filteredPickerTemplates.length === 0 ? <div className="text-center py-12" data-id="element-4399">
                    <p className="text-sm text-gray-500" data-id="element-4400">
                      {templateSearch ? 'No templates match your search.' : 'No form templates available yet.'}
                    </p>
                  </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4401">
                    {filteredPickerTemplates.map(tpl => <button key={tpl.ID} onClick={() => applyFormTemplate(tpl)} className="text-left p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors group" data-id="element-4402">
                        <div className="flex items-start justify-between gap-2 mb-1" data-id="element-4403">
                          <p className="text-sm font-medium text-gray-900 group-hover:text-indigo-900 truncate" data-id="element-4404">
                            {tpl.Template_Name || 'Untitled'}
                          </p>
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${tpl.Builder_Mode === 'builder' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-4405">
                            {tpl.Builder_Mode === 'builder' ? 'Builder' : 'Code'}
                          </span>
                        </div>
                        {tpl.Description && <p className="text-xs text-gray-500 line-clamp-2 mb-2" data-id="element-4406">
                            {tpl.Description}
                          </p>}
                        {tpl.Category && <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600" data-id="element-4407">
                            {tpl.Category}
                          </span>}
                      </button>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}